import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { whatsappLink } from "../lib/api";
import { useLang } from "../lib/LanguageContext";

export default function WhatsAppButton({ number, message }) {
    const { t, lang } = useLang();
    const msg = message || t("message_book");
    const [showBubble, setShowBubble] = useState(false);

    useEffect(() => {
        const dismissed = sessionStorage.getItem("dea_wa_bubble_dismissed");
        if (dismissed) return;
        const timer = setTimeout(() => setShowBubble(true), 6000);
        return () => clearTimeout(timer);
    }, []);

    const dismiss = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowBubble(false);
        sessionStorage.setItem("dea_wa_bubble_dismissed", "1");
    };

    const bubbleText =
        lang === "al"
            ? "Përshëndetje! 👋 A keni pyetje për rezervime?"
            : lang === "it"
              ? "Ciao! 👋 Hai domande sulle prenotazioni?"
              : lang === "de"
                ? "Hallo! 👋 Fragen zur Buchung?"
                : "Hello! 👋 Any questions about bookings?";

    return (
        <div className="fixed bottom-6 right-6 z-40 flex items-end gap-3">
            <AnimatePresence>
                {showBubble && (
                    <motion.a
                        href={whatsappLink(number, msg)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-bubble"
                        initial={{ opacity: 0, x: 20, scale: 0.85 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 20, scale: 0.85 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                        className="hidden sm:flex items-center gap-3 bg-white text-[#1C1C1C] pl-4 pr-3 py-3 rounded-2xl shadow-2xl border border-[#E5E0D8] max-w-[280px] hover:shadow-[0_20px_40px_rgb(0,0,0,0.15)] transition-shadow relative"
                    >
                        <div className="text-sm font-medium leading-snug pr-2">
                            {bubbleText}
                        </div>
                        <button
                            onClick={dismiss}
                            aria-label="close"
                            className="shrink-0 w-6 h-6 rounded-full bg-[#F4EFE6] hover:bg-[#E5E0D8] flex items-center justify-center text-[#1C1C1C]/60"
                        >
                            <X size={12} />
                        </button>
                        <span className="absolute -bottom-1.5 right-8 w-3 h-3 bg-white border-r border-b border-[#E5E0D8] rotate-45" />
                    </motion.a>
                )}
            </AnimatePresence>

            <motion.a
                href={whatsappLink(number, msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-whatsapp"
                aria-label="WhatsApp"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                className="relative bg-[#25D366] hover:bg-[#128C7E] text-white w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center"
            >
                {showBubble && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />
                )}
                <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
            </motion.a>
        </div>
    );
}
